/**
 * F-505-FE-09: api.ts — TanStack Query configuration + authenticated fetch wrapper
 *
 * Key behaviours:
 * - Authorization header injected from AuthContext via getValidToken()
 * - Automatic 401 refresh: on first 401, refresh token → retry original request
 * - Base URL from VITE_API_URL env var (fallback '' for Vite proxy in dev)
 * - Global error handler: toast for server errors, redirect for auth failures
 * - QueryClient with sensible defaults (staleTime 30s, retry 1)
 *
 * This file is the canonical API client for new code. The legacy apiClient.ts
 * (which reads from localStorage) is preserved for backward compat with
 * existing note pages and will be migrated in a future feature.
 *
 * IMPORTANT: apiFetch() references getAuthToken — a module-level callback
 * that must be registered by AuthProvider on mount via registerTokenGetter().
 * This avoids circular context dependencies.
 */

import { QueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Token getter registration (breaks circular dep: api.ts → AuthContext → api.ts)
// ---------------------------------------------------------------------------

type TokenGetter = () => Promise<string | null>;
let _getToken: TokenGetter | null = null;

/** Called by AuthProvider to register the token getter. */
export function registerTokenGetter(getter: TokenGetter): void {
  _getToken = getter;
}

/** Get current auth token for use in non-JSON requests (e.g. multipart uploads). */
export async function getAuthToken(): Promise<string | null> {
  return _getToken ? _getToken() : null;
}

// ---------------------------------------------------------------------------
// Redirect / toast hooks (registered by app-level error boundary)
// ---------------------------------------------------------------------------

type ErrorNotifier = (message: string) => void;
type AuthRedirector = () => void;

let _notifyError: ErrorNotifier = (msg) => console.error('[API Error]', msg);
let _redirectToLogin: AuthRedirector = () => { window.location.href = '/login'; };

export function registerErrorHandlers(
  notifyError: ErrorNotifier,
  redirectToLogin: AuthRedirector,
): void {
  _notifyError = notifyError;
  _redirectToLogin = redirectToLogin;
}

// ---------------------------------------------------------------------------
// API Error class
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// ---------------------------------------------------------------------------
// Base URL
// ---------------------------------------------------------------------------

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

async function executeRequest<T>(
  method: string,
  path: string,
  options: {
    body?: unknown;
    token?: string | null;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (options.token) {
    headers['Authorization'] = `Bearer ${options.token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  const ct = response.headers.get('content-type') ?? '';
  const responseBody: unknown = ct.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof responseBody === 'object' &&
      responseBody !== null &&
      'message' in responseBody
        ? String((responseBody as { message: unknown }).message)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, responseBody);
  }

  return responseBody as T;
}

// ---------------------------------------------------------------------------
// apiFetch — with automatic 401 refresh + retry
// ---------------------------------------------------------------------------

async function apiFetch<T>(
  method: string,
  path: string,
  options: {
    body?: unknown;
    signal?: AbortSignal;
    /** Skip 401 retry (used internally for the refresh attempt itself) */
    skipRefresh?: boolean;
  } = {},
): Promise<T> {
  const token = _getToken ? await _getToken() : null;

  try {
    return await executeRequest<T>(method, path, {
      body: options.body,
      token,
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && !options.skipRefresh) {
      // Attempt token refresh (AuthContext refresh will update _getToken)
      const freshToken = _getToken ? await _getToken() : null;

      if (!freshToken) {
        // Refresh failed — redirect to login
        _redirectToLogin();
        throw err;
      }

      // Retry original request with fresh token
      try {
        return await executeRequest<T>(method, path, {
          body: options.body,
          token: freshToken,
          signal: options.signal,
        });
      } catch (retryErr) {
        if (retryErr instanceof ApiError && retryErr.status === 401) {
          _redirectToLogin();
        }
        throw retryErr;
      }
    }

    // For server errors (5xx), show a toast
    if (err instanceof ApiError && err.status >= 500) {
      _notifyError(err.message || 'A server error occurred. Please try again.');
    }

    throw err;
  }
}

// ---------------------------------------------------------------------------
// Public API surface
// ---------------------------------------------------------------------------

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>('GET', path, { signal }),

  post: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>('POST', path, { body, signal }),

  patch: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>('PATCH', path, { body, signal }),

  put: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>('PUT', path, { body, signal }),

  delete: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>('DELETE', path, { signal }),
};

// ---------------------------------------------------------------------------
// TanStack QueryClient
// ---------------------------------------------------------------------------

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,       // 30 seconds
      gcTime: 5 * 60_000,      // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
